/**
 * Full goal editor modal — create or edit a goal with:
 *   - Name
 *   - Portfolio Group (scope)
 *   - Metric (from measures registry)
 *   - Target value (with direction and unit from the metric)
 *   - Target date (effective_to)
 *   - Description
 *   - Benchmark comparisons (optional)
 *
 * Role (Phase B10): canonical full-page goal CRUD modal. Used by the
 * /exec/goals page. Pair with `GoalEditor.tsx` (compact inline editor used
 * inside the dashboard `GoalsPanel` sidebar) and `GoalsPanel.tsx`
 * (dashboard sidebar surface).
 */
import { useState, useMemo, useEffect } from 'react'
import { X, Target, Save } from 'lucide-react'
import { execAPI } from '../../services/exec'
import type { Goal, GoalTargetValue, MeasureDefinition, PortfolioGroup, BenchmarkSource } from '../../types/exec'
import BenchmarkPicker from './BenchmarkPicker'

interface Props {
  goal: Goal | null
  measures: Record<string, MeasureDefinition>
  onClose: () => void
  /**
   * Optional initial values for first-time goal creation from a starter
   * template (Phase D — see Goals page starter cards). When `goal` is null
   * but `presetGoal` is set, the editor opens in create mode with the
   * preset's measure / target / description pre-filled. The user still
   * picks the portfolio group.
   */
  presetGoal?: Partial<Goal>
}

export default function GoalEditorFull({ goal, measures, onClose, presetGoal }: Props) {
  const isNew = !goal

  const [name, setName] = useState(goal?.name || presetGoal?.name || '')
  const [portfolioGroupId, setPortfolioGroupId] = useState(goal?.portfolio_group || presetGoal?.portfolio_group || '')
  const [measure, setMeasure] = useState(goal?.measure || presetGoal?.measure || '')
  const [targetValue, setTargetValue] = useState(
    goal?.target_value?.value?.toString() || presetGoal?.target_value?.value?.toString() || ''
  )
  const [targetDate, setTargetDate] = useState(goal?.effective_to || presetGoal?.effective_to || '')
  const [description, setDescription] = useState(goal?.description || presetGoal?.description || '')
  const [benchmarks, setBenchmarks] = useState<BenchmarkSource[]>(goal?.benchmark_sources || presetGoal?.benchmark_sources || [])
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState<string | null>(null)

  // Load portfolio groups
  const [portfolioGroups, setPortfolioGroups] = useState<PortfolioGroup[]>([])
  useEffect(() => {
    execAPI.getPortfolioGroups().then(setPortfolioGroups).catch(() => {})
  }, [])

  // Goalable measures (those with a kpi_field defined)
  const goalableMeasures = useMemo(
    () =>
      Object.entries(measures)
        .filter(([_, def]) => def.kpi_field)
        .map(([key, def]) => ({ key, ...def })),
    [measures],
  )

  const selectedMeasure = measures[measure]
  const direction = selectedMeasure?.kpi_direction || 'lower_is_better'
  const unit = selectedMeasure?.kpi_format || 'number'

  // Auto-generate name if empty
  const autoName = useMemo(() => {
    if (!selectedMeasure || !targetValue) return ''
    const prefix = direction === 'lower_is_better' ? '\u2264' : '\u2265'
    const unitStr = unit === 'days' ? ' days' : unit === 'percent' ? '%' : ''
    const dateStr = targetDate ? ` by ${targetDate}` : ''
    return `${selectedMeasure.label} ${prefix} ${targetValue}${unitStr}${dateStr}`
  }, [selectedMeasure, targetValue, direction, unit, targetDate])

  // Auto-suggest benchmarks when portfolio group changes (new goals only)
  useEffect(() => {
    if (portfolioGroupId && benchmarks.length === 0 && isNew) {
      execAPI.suggestBenchmarks(portfolioGroupId)
        .then((resp) => {
          if (resp.suggestions && resp.suggestions.length > 0) {
            setBenchmarks(resp.suggestions)
          }
        })
        .catch(() => {})
    }
  }, [portfolioGroupId])

  const handleSave = async () => {
    if (!measure || !targetValue) return
    if (!portfolioGroupId) {
      setError('Please select a portfolio group')
      return
    }

    setSaving(true)
    setError(null)

    const payload = {
      name: name || autoName,
      portfolio_group: portfolioGroupId,
      office: null as number | null,
      measure,
      target_value: {
        value: parseFloat(targetValue),
        direction,
        unit,
      } as GoalTargetValue,
      benchmark_sources: benchmarks,
      description,
      effective_from: null as string | null,
      effective_to: targetDate || null,
    }

    try {
      if (goal) {
        await execAPI.updateGoal(goal.id, payload)
      } else {
        await execAPI.createGoal(payload)
      }
      onClose()
    } catch (err: any) {
      setError(err.message || 'Failed to save goal')
    } finally {
      setSaving(false)
    }
  }

  const selectClass = 'w-full px-3 py-1.5 text-sm rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-blue-500'

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40" onClick={onClose}>
      <div
        className="bg-white dark:bg-gray-900 rounded-xl shadow-xl w-full max-w-lg max-h-[85vh] flex flex-col overflow-hidden"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between px-4 py-3 border-b border-gray-200 dark:border-gray-700">
          <div className="flex items-center gap-2">
            <Target size={18} className="text-blue-500" />
            <h2 className="text-base font-semibold text-gray-900 dark:text-gray-100">
              {isNew ? 'New Goal' : 'Edit Goal'}
            </h2>
          </div>
          <button onClick={onClose} className="p-1 rounded hover:bg-gray-100 dark:hover:bg-gray-800">
            <X size={18} />
          </button>
        </div>

        {/* Form */}
        <div className="flex-1 overflow-y-auto px-4 py-4 space-y-4">
          {/* Name */}
          <div>
            <label className="block text-xs font-medium text-gray-500 mb-1">Goal Name</label>
            <input
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder={autoName || 'e.g., Speed to Contract < 30 days by FY2027'}
              className={selectClass}
            />
          </div>

          {/* Portfolio Group */}
          <div>
            <label className="block text-xs font-medium text-gray-500 mb-1">Portfolio Group</label>
            <select
              value={portfolioGroupId}
              onChange={(e) => setPortfolioGroupId(e.target.value)}
              className={selectClass}
            >
              <option value="">Select a portfolio group...</option>
              {portfolioGroups.map((pg) => (
                <option key={pg.id} value={pg.id}>
                  {pg.name} ({pg.resolved_office_count} offices)
                </option>
              ))}
            </select>
            <p className="text-[10px] text-gray-400 mt-1">
              <a href="/exec/portfolio-groups" className="text-blue-500 hover:underline">Manage portfolio groups</a>
            </p>
          </div>

          {/* Metric */}
          <div>
            <label className="block text-xs font-medium text-gray-500 mb-1">Metric</label>
            <select
              value={measure}
              onChange={(e) => setMeasure(e.target.value)}
              disabled={!!goal}
              className={`${selectClass} disabled:opacity-50`}
            >
              <option value="">Select a metric...</option>
              {goalableMeasures.map((m) => (
                <option key={m.key} value={m.key}>
                  {m.category}: {m.label}
                </option>
              ))}
            </select>
          </div>

          {/* Target value */}
          {measure && selectedMeasure && (
            <div>
              <label className="block text-xs font-medium text-gray-500 mb-1">
                Target Value
                <span className="text-gray-400 ml-1">
                  ({direction === 'lower_is_better' ? 'lower is better' : 'higher is better'})
                </span>
              </label>
              <div className="flex items-center gap-2">
                <span className="text-sm text-gray-500 font-mono">
                  {direction === 'lower_is_better' ? '\u2264' : '\u2265'}
                </span>
                <input
                  type="number"
                  value={targetValue}
                  onChange={(e) => setTargetValue(e.target.value)}
                  placeholder="e.g., 30"
                  className="flex-1 px-3 py-1.5 text-sm rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800"
                />
                <span className="text-sm text-gray-500">
                  {unit === 'days' && 'days'}
                  {unit === 'percent' && '%'}
                  {unit === 'dollars' && '$'}
                </span>
              </div>
            </div>
          )}

          {/* Target date */}
          <div>
            <label className="block text-xs font-medium text-gray-500 mb-1">Target Date (optional)</label>
            <input
              type="date"
              value={targetDate}
              onChange={(e) => setTargetDate(e.target.value)}
              className={selectClass}
            />
          </div>

          {/* Description */}
          <div>
            <label className="block text-xs font-medium text-gray-500 mb-1">Description (optional)</label>
            <textarea
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              rows={2}
              placeholder="Context for why this goal was set and how it should influence behavior"
              className={`${selectClass} resize-none`}
            />
          </div>

          {/* Benchmark comparisons */}
          <div>
            <label className="block text-xs font-medium text-gray-500 mb-1">Benchmark Comparisons</label>
            <p className="text-[10px] text-gray-400 mb-1.5">
              Compare this goal's metric against other branches, commands, or portfolios.
            </p>
            <BenchmarkPicker
              benchmarks={benchmarks}
              onChange={setBenchmarks}
              excludePortfolioGroupId={portfolioGroupId}
            />
          </div>

          {error && <p className="text-sm text-red-500">{error}</p>}
        </div>

        {/* Footer */}
        <div className="flex justify-end gap-2 px-4 py-3 border-t border-gray-200 dark:border-gray-700">
          <button onClick={onClose} className="px-3 py-1.5 text-sm rounded-lg text-gray-600 hover:bg-gray-100 dark:hover:bg-gray-800">
            Cancel
          </button>
          <button
            onClick={handleSave}
            disabled={saving || !measure || !targetValue || !portfolioGroupId}
            className="flex items-center gap-1.5 px-4 py-1.5 text-sm font-medium rounded-lg bg-blue-600 text-white hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          >
            <Save size={14} />
            {saving ? 'Saving...' : isNew ? 'Create Goal' : 'Save Changes'}
          </button>
        </div>
      </div>
    </div>
  )
}
